import React, { useState, useMemo, useCallback } from "react";
import withNavigate from "../withNavigate";
import ReactSelect from "react-select";
import { BarGraphIcon1, BarGraphIcon2, BarGraphIcon3, DatePickerIcon, SearchIcon } from "../../../components/commonUI/Icons/SvgIcon";
import DatePicker from "react-datepicker";
import FilterContainer from "../../../components/commonUI/SearchFilterBar/FilterContainer";
import moment from "moment-timezone";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import ChartVisualization from "../../../components/commonUI/Charts/ChartVisualization";
import { defaultAvatarImage, imageFormat, serverUrl, thumbSuffix, userImagesFolder } from "../../../config/env";
import TopNav from "../../commonUI/TopHeader/TopHeader";

const RptAccountDeletions = (props) => {
  const [activeTab, setActiveTab] = useState("analytics");
  const [selectedPeriod, setSelectedPeriod] = useState("12M");
  const [selectedInterval, setSelectedInterval] = useState("Month");
  const [selectedChartType, setSelectedChartType] = useState("totals");
  const [chartViewMode, setChartViewMode] = useState("bar");
  const [accountTypeFilter, setAccountTypeFilter] = useState("any");
  const [cancelledByFilter, setCancelledByFilter] = useState("any");
  const [cancelledStartDate, setCancelledStartDate] = useState(null);
  const [cancelledEndDate, setCancelledEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);

  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  // Static data for metrics
  const metricsData = {
    totalDeletions: { value: 165, change: 148, percentage: 871 },
    clientDeletions: { value: 97, change: 86, percentage: 782 },
    driverDeletions: { value: 68, change: 62, percentage: 1033 },
  };

  const handleInputChange = (event) => {
    setSearchValue(event.target.value);
  };

  const searchIssueAndResolution = () => {
    setSearch(searchValue);
  };

  const handleReset = () => {
    setSearchValue("");
    setSearch("");
    setCancelledByFilter("any");
    setJobTypeFilter("any");
    setCancelledStartDate(null);
    setCancelledEndDate(null);
  };

  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showDatePicker) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showDatePicker]);

  // Chart data for different periods
  const chartData = {
    "12M": [
      { month: "Aug 2024", total: 18, client: 11, driver: 7 },
      { month: "Sep 2024", total: 10, client: 6, driver: 4 },
      { month: "Oct 2024", total: 12, client: 7, driver: 5 },
      { month: "Nov 2024", total: 9, client: 5, driver: 4 },
      { month: "Dec 2024", total: 10, client: 6, driver: 4 },
      { month: "Jan 2025", total: 22, client: 13, driver: 9 },
      { month: "Feb 2025", total: 9, client: 5, driver: 4 },
      { month: "Mar 2025", total: 12, client: 7, driver: 5 },
      { month: "Apr 2025", total: 11, client: 7, driver: 4 },
      { month: "May 2025", total: 26, client: 16, driver: 10 },
      { month: "Jun 2025", total: 19, client: 12, driver: 7 },
      { month: "Jul 2025", total: 7, client: 4, driver: 3 },
    ],
  };

  // Static data for records table
  const recordsData = [
    {
      id: 1,
      cancelledBy: {
        name: "John Smith",
        code: "F3R4ZVAUR",
        initials: "JS",
        avatarColor: "teal",
      },
      cancelledOn: { date: "10 Jun 25", time: "14:30" },
      reason: "Found a better deal elsewhere",
      clientPaid: "£50.00",
      jobType: "House Move",
      scheduledFor: { date: "15 Jun 25", time: "09:00" },
      timeFromJob: "4d before",
      accountAge: "1 year, 9 months, 13 days",
    },
    {
      id: 2,
      cancelledBy: {
        name: "Sarah Williams",
        code: "Z816ASDFG",
        initials: "SW",
        avatarColor: "teal",
      },
      cancelledOn: { date: "8 Jun 25", time: "09:15" },
      reason: "No longer need the service",
      clientPaid: "£35.00",
      jobType: "Send Items",
      scheduledFor: { date: "9 Jun 25", time: "13:30" },
      timeFromJob: "1d before",
      accountAge: "	9 months, 1 day",
    },
    {
      id: 3,
      cancelledBy: {
        name: "Emma Davis",
        code: "M2T3QWERT",
        initials: "ED",
        avatarColor: "gray",
      },
      cancelledOn: { date: "5 Jun 25", time: "16:45" },
      reason: "Change of plans, moving date postponed",
      clientPaid: "£75.00",
      jobType: "House Move",
      scheduledFor: { date: "12 Jun 25", time: "10:00" },
      timeFromJob: "6d before",
      accountAge: "2 years, 11 months, 7 days",
    },
    {
      id: 4,
      cancelledBy: {
        name: "Mike Johnson",
        code: "K9P7XBCDE",
        initials: "MJ",
        avatarColor: "blue",
      },
      cancelledOn: { date: "3 Jun 25", time: "11:20" },
      reason: "Vehicle breakdown - unable to complete job",
      clientPaid: "£25.00",
      jobType: "Waste Disposal",
      scheduledFor: { date: "2 Jun 25", time: "14:00" },
      timeFromJob: "21h after",
      accountAge: "1 year, 10 months, 13 days",
    },
    {
      id: 5,
      cancelledBy: {
        name: "Sophia Martin",
        code: "H6G5F4D3S",
        initials: "SM",
        avatarColor: "teal",
      },
      cancelledOn: { date: "30 May 25", time: "08:10" },
      reason: "Items no longer need to be sent",
      clientPaid: "£40.00",
      jobType: "Send Items",
      scheduledFor: { date: "1 Jun 25", time: "11:30" },
      timeFromJob: "2d before",
      accountAge: "1 year, 10 months, 13 days",
    },
    {
      id: 6,
      cancelledBy: {
        name: "Jacob Anderson",
        code: "J991BG7FG3",
        initials: "JA",
        avatarColor: "orange",
      },
      cancelledOn: { date: "28 May 25", time: "13:05" },
      reason: "Found alternative service",
      clientPaid: "£60.00",
      jobType: "House Move",
      scheduledFor: { date: "27 May 25", time: "09:15" },
      timeFromJob: "1d after",
      accountAge: "1 year, 10 months, 13 days",
    },
    {
      id: 7,
      cancelledBy: {
        name: "Mia Thompson",
        code: "M224P9Q2L",
        initials: "MT",
        avatarColor: "purple",
      },
      cancelledOn: { date: "25 May 25", time: "15:40" },
      reason: "Waste disposal no longer needed",
      clientPaid: "£30.00",
      jobType: "Waste Disposal",
      scheduledFor: { date: "26 May 25", time: "16:00" },
      timeFromJob: "19h before",
      accountAge: "1 year, 10 months, 13 days",
    },
  ];

  // Period buttons configuration
  const periodButtons = [
    { id: "custom", label: "Custom range" },
    { id: "7D", label: "7D" },
    { id: "30D", label: "30D" },
    { id: "3M", label: "3M" },
    { id: "6M", label: "6M" },
    { id: "12M", label: "12M" },
  ];

  // Interval options
  const intervalOptions = [
    { value: "Day", label: "Day" },
    { value: "Week", label: "Week" },
    { value: "Month", label: "Month" },
    { value: "Year", label: "Year" },
  ];

  // Chart type options
  const chartTypeOptions = [
    { value: "totals", label: "Totals" },
    { value: "clients", label: "Client Deletions" },
    { value: "drivers", label: "Driver Deletions" },
  ];


  const accountTypeOptions = [
    {
      value: "any",
      label: `Any `,
    },
    {
      value: "client",
      label: `Client `,
    },
    {
      value: "mav",
      label: `Mav `,
    },

  ];

  const selectedAccountTypeOption = accountTypeOptions.find(
    (option) => option.value === accountTypeFilter
  );

  const selectStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        minHeight: "40px",
        height: "40px",
        minWidth: "200px",
        width: "200px",
        padding: "0",
        boxShadow: "none",
        backgroundColor: "#FFFFFF",
        "&:focus-visible": {
          borderColor: "#000000",
          boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #000000",
          outline: "none",
        },
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: "40px",
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
      }),
      input: (provided) => ({
        ...provided,
        margin: "0",
        padding: "0",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: "40px",
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        zIndex: 1000,
      }),
      menuList: (provided) => ({
        ...provided,
        backgroundColor: "#FFFFFF",
        padding: "0",
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "#e0f2fe"
          : state.isFocused
          ? "#f1f5f9"
          : "#FFFFFF",
        color: "#333",
        cursor: "pointer",
        pointerEvents: "auto",
        padding: "10px 12px",
        "&:active": {
          backgroundColor: "#e0f2fe",
        },
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
      }),
    }),
    []
  );


  const getAvatarSrc = useCallback((record) => {
    if (record.cancelledBy.avatar && record.cancelledBy.avatar !== "") {
      return `${serverUrl}${userImagesFolder}/${record?.cancelledBy?.shortId}/${record.cancelledBy.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  // Get current chart data based on selected period
  const getCurrentChartData = () => {
    return chartData[selectedPeriod] || chartData["12M"];
  };

  // Get chart values based on selected chart type
  const getChartValues = (data) => {
    switch (selectedChartType) {
      case "clients":
        return data.map((item) => item.client);
      case "drivers":
        return data.map((item) => item.driver);
      default:
        return data.map((item) => item.total);
    }
  };

  const handleChartMouseEnter = (event, item, value, tooltipData) => {
    const rect = event.currentTarget?.getBoundingClientRect?.() || event.target.getBoundingClientRect();
    let label = "total";
    switch (selectedChartType) {
      case "clients":
        label = "client";
        break;
      case "drivers":
        label = "driver";
        break;
      default:
        label = "total";
    }
    setTooltip({
      visible: true,
      x: event.clientX || rect.left + rect.width / 2,
      y: event.clientY || rect.top,
      data: tooltipData || { month: item.month, value, type: label },
    });
  };

  const handleChartMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, data: null });
  };

  const renderAnalyticsTab = () => (
    <div style={{ background: "white", border: "1px solid #e5e7eb", padding: "24px" }}>
      <div className="booking-analytics">
        {/* Time Period Controls */}
        <div className="analytics-controls">
          <div className="period-buttons-wrapper">
            <div className="custom-range-dropdown">
              <button
                className="period-btn custom-range-btn"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                {cancelledStartDate && cancelledEndDate
                  ? `${moment(cancelledStartDate).format("DD MMM YYYY")} - ${moment(cancelledEndDate).format("DD MMM YYYY")}`
                  : "Custom range"
                }
                {cancelledStartDate && cancelledEndDate && (
                  <div
                    className="close-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCancelledStartDate(null);
                      setCancelledEndDate(null);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </div>
                )}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: "4px" }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {showDatePicker && (
                <div className="date-picker-backdrop" onClick={() => setShowDatePicker(false)} />
              )}
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <DatePicker
                    selectsRange
                    className="filter-box date-picker"
                    startDate={cancelledStartDate}
                    endDate={cancelledEndDate}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setCancelledStartDate(start);
                      setCancelledEndDate(end);
                      if (start && end) {
                        setShowDatePicker(false);
                      }
                    }}
                    placeholderText="Anytime"
                    monthsShown={2}
                    inline
                    showIcon={false}
                  />
                </div>
              )}
            </div>
            <div className="period-buttons">
              {periodButtons.slice(1).map((button) => (
                <button
                  key={button.id}
                  className={`period-btn ${
                    selectedPeriod === button.id ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedPeriod(button.id);
                    setShowDatePicker(false);
                  }}
                >
                  {button.label}
                </button>
              ))}
            </div>
          </div>

          <div className="interval-selector">
            <span className="interval-label">Interval:</span>
            <ReactSelect
              value={intervalOptions.find(
                (opt) => opt.value === selectedInterval
              )}
              onChange={(option) => setSelectedInterval(option.value)}
              options={intervalOptions}
              styles={selectStyles}
              isSearchable={false}
              className="interval-select"
            />
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="metrics-cards">
          <div className="metric-card">
            <div className="metric-title">Total Deletions</div>
            <div className="metric-value-row">
              <div className="metric-value">
                {metricsData?.totalDeletions?.value}
              </div>
              <div className="metric-change positive">
                +{metricsData?.totalDeletions?.change} (
                {metricsData?.totalDeletions?.percentage}%)
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Client Deletions</div>
            <div className="metric-value-row">
              <div className="metric-value">
                {metricsData?.clientDeletions?.value}
              </div>
              <div className="metric-change positive">
                +{metricsData?.clientDeletions?.change} (
                {metricsData?.clientDeletions?.percentage}%)
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Driver Deletions</div>
            <div className="metric-value-row">
              <div className="metric-value">
                {metricsData?.driverDeletions?.value}
              </div>
              <div className="metric-change positive">
                +{metricsData?.driverDeletions?.change} (
                {metricsData?.driverDeletions?.percentage}%)
              </div>
            </div>
          </div>
        </div>

        {/* Chart Section */}
        <div className="chart-section" style={{ background: "transparent", border: "none", padding: "0" }}>
          <div className="chart-controls">
            <ReactSelect
              value={chartTypeOptions.find(
                (opt) => opt.value === selectedChartType
              )}
              onChange={(option) => setSelectedChartType(option.value)}
              options={chartTypeOptions}
              styles={selectStyles}
              isSearchable={false}
              className="chart-select"
            />
            <div className="chart-view-options">
              <button
                className={`chart-btn ${chartViewMode === "bar" ? "active" : ""}`}
                onClick={() => setChartViewMode("bar")}
                title="Bar Chart"
              >
                <BarGraphIcon1/>
              </button>
              <button
                className={`chart-btn ${chartViewMode === "area" ? "active" : ""}`}
                onClick={() => setChartViewMode("area")}
                title="Area Chart"
              >
                <BarGraphIcon2/>
              </button>
              <button
                className={`chart-btn ${
                  chartViewMode === "line" ? "active" : ""
                }`}
                onClick={() => setChartViewMode("line")}
                title="Line Chart"
              >
                <BarGraphIcon3/>
              </button>
             
            </div>
          </div>

          <div className="chart-content">
            {chartViewMode === "bar" && (
              <ChartVisualization
                data={getCurrentChartData()}
                chartType={`bar-${selectedChartType}`}
                tooltip={tooltip}
                onBarMouseEnter={handleChartMouseEnter}
                onBarMouseLeave={handleChartMouseLeave}
              />
            )}
            {chartViewMode === "line" && (
              <ChartVisualization
                data={getCurrentChartData()}
                chartType={`line-${selectedChartType}`}
                tooltip={tooltip}
                onBarMouseEnter={handleChartMouseEnter}
                onBarMouseLeave={handleChartMouseLeave}
              />
            )}
            {chartViewMode === "area" && (
              <ChartVisualization
                data={getCurrentChartData()}
                chartType={`area-${selectedChartType}`}
                tooltip={tooltip}
                onBarMouseEnter={handleChartMouseEnter}
                onBarMouseLeave={handleChartMouseLeave}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRecordsTab = () => (
    <div>
      <div className="mb-24">
        <div className="mb-24 filter-container">
          {/* Search */}
          <div className="lh-1">
            <label htmlFor="search" className="form-label">
              Search
            </label>
            <div className="search-box">
              <input
                type="text"
                value={searchValue || ""}
                onChange={handleInputChange}
                name="search"
                className="search-input"
                placeholder="User or reason..."
                id="search"
                onKeyDown={(e) =>
                  e.key === "Enter" && searchIssueAndResolution()
                }
              />
              <SearchIcon />
            </div>
          </div>
      
          {/* Job Type Filter */}
          <div className="lh-1">
            <label htmlFor="accountType" className="form-label">
              Account Type
            </label>

            <ReactSelect
              id="accountType"
              name="accountType"
              classNamePrefix="custom-select"
              placeholder="Select Account Type"
              options={accountTypeOptions}
              value={selectedAccountTypeOption}
              onChange={(selectedOption) => {
                setAccountTypeFilter(selectedOption ? selectedOption.value : "all");
              }}
              className="react-select-container"
              styles={selectStyles}
            />
          </div>
          <div className="lh-1">
            <label htmlFor="datetimePicker" className="form-label">
            Deleted date
            </label>
            {/* <input type="date" name="datetimePicker" placeholder="Anytime" className="filter-box date-picker" /> */}
            <DatePicker
              showIcon
              selectsRange
              className="filter-box date-picker"
              startDate={cancelledStartDate}
              endDate={cancelledEndDate}
              onChange={(dates) => {
                const [start, end] = dates;
                setCancelledStartDate(start);
                setCancelledEndDate(end);
              }}
              placeholderText="Anytime"
              monthsShown={2}
              icon={
                <DatePickerIcon className="react-datepicker__calendar-icon" />
              }
            />
          </div>
          <FilterContainer onReset={handleReset} />{" "}
        </div>
      </div>
      <div className="table-responsive">
        <table className="table clientTable">
          <thead>
            <tr>
              <th>Deleted Account</th>
              <th>Deleted On</th>
              <th>Reason</th>
              <th>Account Age</th>
              <th></th>
             
            </tr>
          </thead>
          <tbody>
            {recordsData?.map((record) => (
              <tr key={record.id}>
                <td>
                  <div className="d-flex align-items-center g-12">
                    <img
                      className="avatar-pic"
                      alt="avatar"
                      src={getAvatarSrc(record)}
                      onError={(e) => {
                        e.target.src = defaultAvatarImage;
                      }}
                    />
                    <div className="text-capitalize">
                      {record?.cancelledBy?.name || "-"}
                      <br />
                      <span className="text-sm text-gray-600 ">
                        {record?.cancelledBy?.code}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  {moment(record?.cancelledOn?.date)
                    ?.tz("Europe/London")
                    ?.format("DD MMM YYYY")}

                  <br />
                  <span className="text-sm text-gray-600 ">
                    {moment(record?.cancelledOn?.date)
                      ?.tz("Europe/London")
                      ?.format("hh:mm ")}
                  </span>
                </td>
                <td>{record.reason}</td>
                <td>{record.accountAge}</td>
             
                <td>
                  <button className="custom-btn">View Profile</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
    </div>
  );

  return (
    <>
      <TopNav title={"Account Deletions"} />
      <div className="global-body">
        <div className="global-body-container">
          {/* Common Container Wrapper */}
          <div>
            {/* Tab Navigation */}
            <div className="custom-tabs mt-0">
              <button
                className={`tab-item ${
                  activeTab === "analytics" ? "active" : ""
                }`}
                onClick={() => setActiveTab("analytics")}
              >
                Analytics
              </button>
              <button
                className={`tab-item ${
                  activeTab === "records" ? "active" : ""
                }`}
                onClick={() => setActiveTab("records")}
              >
                Records
              </button>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === "analytics"
                ? renderAnalyticsTab()
                : renderRecordsTab()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withNavigate(RptAccountDeletions);
